(function () {
  'use strict';

  /**
   * <cart-upsell-carousel>
   *
   * theme.js's renderPairProducts() unconditionally initializes a Flickity
   * carousel on [data-pair-products] whenever 2+ upsell cards exist, with
   * a hardcoded, non-configurable cellAlign:'center' + wrapAround:true
   * (confirmed live: flkty.options.cellAlign still read 'center' even with
   * a data-flickity-options attribute present — this theme's Flickity
   * wrapper doesn't honor that standard override mechanism). Centered
   * alignment means the selected card can never sit flush against the
   * left edge with a peek on the right only, which is what the mockup
   * this is built against calls for.
   *
   * theme.js is off-limits to edit, so this doesn't try to reconfigure or
   * replace its Flickity call — it lets that call happen (still required:
   * data-pair-products / data-pair-products-holder / data-upsell-holder
   * all stay in the markup because theme.js also uses them for unrelated,
   * necessary things — hiding this whole section when the cart empties,
   * clearing it, and scoping each upsell "+" button's loading/success
   * state to its own card — removing any of them to dodge Flickity would
   * break those), then immediately destroys the Flickity instance it
   * created and hands the element back to plain CSS scroll-snap (see
   * .cart-upsell-carousel__track / .product-upsell__holder--cart's
   * scroll-snap-align in cart-drawer.css / template-cart.css) — the same
   * "independent component alongside theme.js" pattern already used by
   * cart-progress-tiers.js and buy-button-proxy.js elsewhere in this
   * project.
   *
   * Timing: theme.js calls renderPairProducts() (which does the Flickity
   * init) synchronously, then dispatches theme:cart:added / :refresh /
   * :update right after — so listening for those events already runs
   * after Flickity exists. Every sync is deferred one more
   * requestAnimationFrame regardless, as a safety margin against any
   * initialization theme.js does asynchronously.
   */
  customElements.get('cart-upsell-carousel') ||
    customElements.define(
      'cart-upsell-carousel',
      class extends HTMLElement {
        connectedCallback() {
          this.boundSync = () => requestAnimationFrame(() => this.sync());
          ['theme:cart:added', 'theme:cart:refresh', 'theme:cart:update'].forEach((eventName) => {
            document.addEventListener(eventName, this.boundSync);
          });
          this.boundSync();
        }

        disconnectedCallback() {
          ['theme:cart:added', 'theme:cart:refresh', 'theme:cart:update'].forEach((eventName) => {
            document.removeEventListener(eventName, this.boundSync);
          });
          this.observer?.disconnect();
        }

        sync() {
          const track = this.querySelector('[data-pair-products]');
          if (!track) return;

          const flkty = window.theme?.Flickity?.data?.(track);
          if (flkty) flkty.destroy();

          track.classList.add('cart-upsell-carousel__track');
          this.observeCards(track);
        }

        observeCards(track) {
          const cards = Array.from(track.querySelectorAll(':scope > [data-upsell-holder]'));
          const dots = Array.from(this.querySelectorAll('[data-upsell-dots] .cart-upsell-carousel__dot'));

          this.observer?.disconnect();
          if (!cards.length || !dots.length) return;

          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const index = cards.indexOf(entry.target);
                if (index === -1) return;
                dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
              });
            },
            { root: track, threshold: 0.6 }
          );
          cards.forEach((card) => this.observer.observe(card));
        }
      }
    );
})();
